'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { CapabilitySet } from '@/lib/config/capability-catalog';
import type { ExperienceId } from '@/lib/services/experience-service';

export type EntitlementsState = {
  plan: string;
  capabilities: CapabilitySet | null;
  experience: ExperienceId | null;
  showUpgrade: boolean;
  activeTrial: {
    id: string;
    type: string;
    status: string;
    endsAt: string;
    grants: string[];
  } | null;
  latestTrial: {
    id: string;
    type: string;
    status: string;
    endsAt: string;
  } | null;
  loading: boolean;
  refresh: () => Promise<void>;
};

const EntitlementsContext = createContext<EntitlementsState | null>(null);

const emptyState: Omit<EntitlementsState, 'refresh' | 'loading'> = {
  plan: 'FREE',
  capabilities: null,
  experience: null,
  showUpgrade: true,
  activeTrial: null,
  latestTrial: null,
};

export function EntitlementsProvider({
  children,
  enabled,
}: {
  children: ReactNode;
  enabled: boolean;
}) {
  const [state, setState] = useState(emptyState);
  const [loading, setLoading] = useState(enabled);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = await fetch('/api/entitlements', { cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      setState({
        plan: data.plan,
        capabilities: data.capabilities,
        experience: data.experience,
        showUpgrade: Boolean(data.showUpgrade),
        activeTrial: data.activeTrial ?? null,
        latestTrial: data.latestTrial ?? null,
      });
    } catch {
      // Keep last known state on transient failures
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    void refresh();
  }, [enabled, refresh]);

  return (
    <EntitlementsContext.Provider value={{ ...state, loading, refresh }}>
      {children}
    </EntitlementsContext.Provider>
  );
}

export function useEntitlements(): EntitlementsState {
  const ctx = useContext(EntitlementsContext);
  if (!ctx) {
    return {
      ...emptyState,
      loading: false,
      refresh: async () => undefined,
    };
  }
  return ctx;
}
